// src/screens/protected/ProjecGroups.js
import React, { useEffect, useMemo, useState } from "react";
import { Nav } from "../../components/nav";
import { useAuth } from "../../navigation/AuthProvider";
import { Toast } from "../../components/ToastPopup";
import { BACKEND_URL } from "../../constants";
import axios from "axios";
import {
  FaPlus,
  FaLayerGroup,
  FaEdit,
  FaTrash,
  FaSearch,
  FaChevronDown,
  FaTimes, // ⬅️ for removing a selected project chip
  FaUserPlus, // ⬅️ NEW: assign users to group
} from "react-icons/fa";

import { getPermission } from "../../permissions/rolePermissions";

// -------------------------------------------------------------
// Projektgrupper – översikt & enkel CRUD (+ Tilldelning av användare)
// -------------------------------------------------------------

const ACCENT = "#5caef6ff";
const BORDER = "#E5E7EB";
const TEXT_MUTED = "#6B7280";

const countBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 8px",
  height: 22,
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  background: "#F3F4F6",
  color: "#374151",
};

export const ProjecGroups = () => {
  const {
    projectGroups = [],
    setProjectGroups,
    projects = [],
    setProjects,
    company,
    user,
    employees = [], // ⬅️ NEW: used for assignment modal
  } = useAuth();

    const employeesPermission = getPermission(user.role, "projects:groups");
    const canEditProjects = employeesPermission === "edit"

  // ------ UI state ------
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  const [allGroups, setAllGroups] = useState(projectGroups || []);
  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState(allGroups);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  const [groupName, setGroupName] = useState("");
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [projectFilter, setProjectFilter] = useState("");

  // ------ NEW: Assign modal state ------
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [assignGroup, setAssignGroup] = useState(null);
  const [userFilter, setUserFilter] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  // Keep local list in sync if provider updates
  useEffect(() => {
    setAllGroups(projectGroups || []);
  }, [projectGroups]);

  // Filter groups by search
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) return setFiltered(allGroups);
    setFiltered(allGroups.filter((g) => g?.name?.toLowerCase().includes(q)));
  }, [search, allGroups]);

  // ------ Helpers (projects) ------
  const projectById = useMemo(() => {
    const map = new Map();
    for (const p of projects) map.set(String(p._id), p);
    return map;
  }, [projects]);

  const projectOptions = useMemo(() => {
    const q = projectFilter.trim().toLowerCase();
    const base = projects || [];
    if (!q) return base;
    return base.filter((p) => p?.name?.toLowerCase().includes(q));
  }, [projectFilter, projects]);

  const selectedProjects = useMemo(() => {
    // Sorted list of selected projects to show clearly in the popup
    const list = selectedProjectIds
      .map((id) => projectById.get(String(id)))
      .filter(Boolean);
    return list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "sv"));
  }, [selectedProjectIds, projectById]);

  const previewProjectNames = (ids = [], max = 3) => {
    const names = [];
    for (const id of ids) {
      const p = projectById.get(String(id));
      if (p?.name) names.push(p.name);
    }
    names.sort((a, b) => a.localeCompare(b, "sv"));
    const extra = Math.max(0, names.length - max);
    const shown = names.slice(0, max);
    return { shown, extra };
  };

  const resetForm = () => {
    setEditingGroup(null);
    setGroupName("");
    setSelectedProjectIds([]);
    setProjectFilter("");
  };

  const openCreate = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEdit = (g) => {
    setEditingGroup(g);
    setGroupName(g?.name || "");
    setSelectedProjectIds((g?.projectIds || []).map(String));
    setProjectFilter("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const toggleProjectSelection = (id) => {
    setSelectedProjectIds((prev) => {
      const s = new Set(prev.map(String));
      const key = String(id);
      if (s.has(key)) s.delete(key);
      else s.add(key);
      return Array.from(s);
    });
  };

  // ------ API actions (backend endpoints kan anpassas) ------
  const createGroup = async () => {
    try {
      const resp = await axios.post(`${BACKEND_URL}/project-groups/create`, {
        companyId: company?._id,
        userId: user?._id,
        name: groupName.trim(),
        projectIds: selectedProjectIds,
      });
      const data = resp?.data || {};
      if (data.status === "success") {
        if (setProjectGroups) {
          const next =
            data.groups ||
            (allGroups && data.group ? [...allGroups, data.group] : allGroups);
          setProjectGroups(next);
          setAllGroups(next);
          setFiltered(next);
        }
        setToastType("success");
        setToastMessage("Projektgrupp skapad!");
        closeModal();
      } else {
        throw new Error(data?.errorText || "Kunde inte skapa gruppen.");
      }
    } catch (e) {
      setToastType("error");
      setToastMessage(e.message || "Fel vid skapande.");
    }
  };

  const updateGroup = async () => {
    try {
      const resp = await axios.post(`${BACKEND_URL}/project-groups/update`, {
        userId: user?._id,
        companyId: company?._id,
        groupId: editingGroup?._id,
        name: groupName.trim(),
        projectIds: selectedProjectIds,
      });
      const data = resp?.data || {};
      if (data.status === "success") {
        const updated = data.group || {
          ...editingGroup,
          name: groupName.trim(),
          projectIds: selectedProjectIds,
          updatedAt: new Date().toISOString(),
        };
        const next = (allGroups || []).map((g) =>
          String(g._id) === String(updated._id) ? updated : g
        );
        if (setProjectGroups) setProjectGroups(next);
        setAllGroups(next);
        setFiltered(next);
        setToastType("success");
        setToastMessage("Projektgrupp uppdaterad!");
        closeModal();
      } else {
        throw new Error(data?.errorText || "Kunde inte uppdatera gruppen.");
      }
    } catch (e) {
      setToastType("error");
      setToastMessage(e.message || "Fel vid uppdatering.");
    }
  };

  const deleteGroup = async (g) => {
    const ok = window.confirm(
      `Radera grupp "${g?.name}"? Detta tar inte bort några projekt.`
    );
    if (!ok) return;
    try {
      const resp = await axios.post(`${BACKEND_URL}/project-groups/delete`, {
        companyId: company?._id,
        groupId: g?._id,
        userId: user?._id,
      });
      const data = resp?.data || {};
      if (data.status === "success") {
        const next = (allGroups || []).filter(
          (x) => String(x._id) !== String(g._id)
        );
        if (setProjectGroups) setProjectGroups(next);
        setAllGroups(next);
        setFiltered(next);
        setToastType("success");
        setToastMessage("Projektgrupp raderad.");
        closeModal();
      } else {
        throw new Error(data?.errorText || "Kunde inte radera gruppen.");
      }
    } catch (e) {
      setToastType("error");
      setToastMessage(e.message || "Fel vid radering.");
    }
  };

  // ------ Validation helper ------
  const ensureOneProjectSelected = () => {
    if (!selectedProjectIds || selectedProjectIds.length === 0) {
      setToastType("error");
      setToastMessage("Välj minst ett projekt i gruppen.");
      return false;
    }
    return true;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!groupName.trim()) {
      setToastType("error");
      setToastMessage("Ange ett namn för gruppen.");
      return;
    }
    if (!ensureOneProjectSelected()) return;

    if (editingGroup) updateGroup();
    else createGroup();
  };

  // ------ NEW: Assignment helpers ------
  const openAssign = (group) => {
    setAssignGroup(group);
    setIsAssignOpen(true);
    setUserFilter("");
    setSelectedUserIds([]); // fresh selection each time
  };

  const closeAssign = () => {
    setIsAssignOpen(false);
    setAssignGroup(null);
    setUserFilter("");
    setSelectedUserIds([]);
  };

  const filteredEmployees = useMemo(() => {
    const list = employees || [];
    const q = userFilter.trim().toLowerCase();
    if (!q) return list;
    return list.filter((e) => {
      const n = `${e?.name || ""}`.toLowerCase();
      const mail = `${e?.email || ""}`.toLowerCase();
      return n.includes(q) || mail.includes(q);
    });
  }, [employees, userFilter]);

  const toggleUserSelection = (id) => {
    setSelectedUserIds((prev) => {
      const s = new Set(prev.map(String));
      const key = String(id);
      if (s.has(key)) s.delete(key);
      else s.add(key);
      return Array.from(s);
    });
  };


  // Replace/merge updated projects into local state by _id
    const mergeUpdatedProjects = (prev = [], incoming = []) => {
    if (!Array.isArray(incoming) || incoming.length === 0) return prev;
    const map = new Map(prev.map(p => [String(p._id), p]));
    for (const up of incoming) {
        map.set(String(up._id), { ...(map.get(String(up._id)) || {}), ...up });
    }
    return Array.from(map.values());
    };


    const assignUsersToGroup = async () => {
    if (!assignGroup?._id) return;
    if (!selectedUserIds.length) {
        setToastType("error");
        setToastMessage("Välj minst en användare att tilldela.");
        return;
    }
    try {
        const resp = await axios.post(`${BACKEND_URL}/project-groups/assign-users`, {
        companyId: company?._id,
        groupId: assignGroup._id,
        userIds: selectedUserIds,
        userId: user?._id,
        mode: "add",
        });

        const data = resp?.data || {};
        const updated = Array.isArray(data?.updatedProjects) ? data.updatedProjects : [];

        // Always merge fresh project docs if backend sent them
        if (setProjects && updated.length) {
        setProjects(prev => mergeUpdatedProjects(prev, updated));
        }

        // Log rich details to dev console for debugging
        if (Array.isArray(data?.perProjectReport)) {
        console.groupCollapsed(
            `[ProjectGroups] Assign report: ${assignGroup?.name || ""}`
        );
        try {
            console.table(
            data.perProjectReport.map(r => ({
                projectId: r.projectId,
                name: r.name,
                pre: r.preCount,
                add: r.addCount,
                skip: r.skipCount,
                post: r.postCount,
            }))
            );
        } catch {
            console.log("perProjectReport:", data.perProjectReport);
        }
        console.log("summary:", data.summary);
        console.groupEnd();
        }

        if (data.status === "success") {
        const s = data?.summary || {};
        const msg = data?.message ||
            `Tilldelade användare till ${s.projectsChanged || 0} av ${s.targetedProjects || 0} projekt (${s.linksAdded || 0} nya kopplingar).`;
        setToastType("success");
        setToastMessage(msg);
        closeAssign();
        return;
        }

        // Failure path: show backend reason; still keep UI in sync (we already merged any updatedProjects above)
        const reason = data?.errorText || "Kunde inte tilldela användare.";
        setToastType("error");
        setToastMessage(reason);

        // Optional: keep the modal open so the user can adjust selection
        // closeAssign(); // <-- leave commented if you want to keep it open on error
    } catch (e) {
        setToastType("error");
        setToastMessage(e?.message || "Fel vid tilldelning.");
    }
    };


  // ------ Render ------
  return (
    <div className="projects-page" style={{ padding: 20, minHeight: "100vh" }}>
      <Nav />

      {/* Header */}
      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background:
                "linear-gradient(135deg, rgba(139,92,246,.2), rgba(59,130,246,.15))",
              display: "grid",
              placeItems: "center",
              border: `1px solid ${BORDER}`,
            }}
          >
            <FaLayerGroup style={{ color: ACCENT, opacity: 0.9 }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h1 style={{ fontSize: "1.6rem", color: "#1F2937", margin: 0 }}>
              Projektgrupper
            </h1>
            <p style={{ margin: 0, marginTop: 4, fontSize: 12, color: TEXT_MUTED }}>
                Samla flera projekt i grupper och tilldela dem till användare för enklare och snabbare hantering.
            </p>
          </div>
        </div>

        {canEditProjects && (
          <button
            className="add-button"
            onClick={openCreate}
            style={{
              backgroundColor: ACCENT,
              color: "#fff",
              fontWeight: 700,
              padding: "10px 16px",
              border: "none",
              borderRadius: 10,
              cursor: "pointer",
              minWidth: 180,
              boxShadow: "0 4px 12px rgba(92,174,246,.25)",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <FaPlus style={{ fontSize: 12 }} />
            Ny grupp
          </button>
        )}
      </div>

      {/* Sub header / search */}
      <div
        style={{
          background: "#fff",
          border: `1px solid ${BORDER}`,
          borderRadius: 12,
          padding: 12,
          marginBottom: 14,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div style={{ position: "relative", flex: 1, maxWidth: 420 }}>
          <FaSearch
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: TEXT_MUTED,
              fontSize: 13,
            }}
          />
          <input
            type="text"
            placeholder="sök grupp"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              height: 34,
              padding: "0 12px 0 30px",
              borderRadius: 8,
              border: `1px solid ${BORDER}`,
              fontSize: 13,
              outline: "none",
            }}
          />
        </div>

        <div
          style={{
            marginLeft: "auto",
            fontSize: 13,
            color: TEXT_MUTED,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>Totalt</span>
          <span style={countBadgeStyle}>{allGroups?.length || 0}</span>
        </div>
      </div>

      {/* List headers */}
      <div
        className="column-headers"
        style={{
          display: "grid",
          gridTemplateColumns: "1.5fr 1fr 0.8fr 80px", // ⬅️ widened a bit to fit two small action buttons
          padding: "10px 12px",
          background: "#fff",
          border: `1px dashed ${BORDER}`,
          borderRadius: 8,
          color: "#374151",
          fontWeight: 600,
          marginBottom: 6,
        }}
      >
        <p style={{ margin: 0 }}>Namn</p>
        <p style={{ margin: 0 }}>Projekt</p>
        <p style={{ margin: 0, textAlign: "left" }}>Senast uppdaterad</p>
        <div style={{ textAlign: "right" }}>Åtgärder</div>
      </div>

      {/* List */}
      <div className="groups-list">
        {filtered.length === 0 ? (
          <div
            style={{
              padding: 16,
              color: TEXT_MUTED,
              fontSize: 14,
              background: "#fff",
              border: `1px solid ${BORDER}`,
              borderRadius: 10,
            }}
          >
            Inga projektgrupper just nu.
          </div>
        ) : (
          filtered.map((g) => {
            const ids = (g?.projectIds || []).map(String);
            const { shown, extra } = previewProjectNames(ids, 3);
            const updatedAt = g?.updatedAt ? new Date(g.updatedAt) : null;

            return (
              <div
                key={g._id}
                className="group-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 1fr 0.8fr 80px",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 12px",
                  background: "#fff",
                  borderRadius: 8,
                  borderLeft: `4px solid ${ACCENT}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,.06)",
                  position: "relative",
                  marginTop: 6,
                }}
              >
                {/* Name */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      background: "rgba(92,174,246,.15)",
                      display: "grid",
                      placeItems: "center",
                      color: ACCENT,
                    }}
                  >
                    <FaLayerGroup style={{ fontSize: 12 }} />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                      style={{ fontSize: 14, color: "#111827", fontWeight: 600 }}
                    >
                      {g?.name || "Namnlös grupp"}
                    </span>
                    <span style={{ fontSize: 12, color: TEXT_MUTED }}>
                      {ids.length} projekt
                    </span>
                  </div>
                </div>

                {/* Projects preview */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  {shown.map((n) => (
                    <span
                      key={n}
                      style={{
                        fontSize: 12,
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: "#F3F4F6",
                        border: `1px solid ${BORDER}`,
                        color: "#374151",
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={n}
                    >
                      {n}
                    </span>
                  ))}
                  {extra > 0 && <span style={countBadgeStyle}>+{extra}</span>}
                </div>

                {/* Updated */}
                <div style={{ fontSize: 12, color: TEXT_MUTED }}>
                  {updatedAt
                    ? updatedAt.toLocaleDateString("sv-SE", {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                      })
                    : "—"}
                </div>

                {/* Actions: Edit + Assign */}
                <div style={{ justifySelf: "end", display: "flex", gap: 8 }}>
                  {canEditProjects && (
                    <>
                      <button
                        onClick={() => openAssign(g)}
                        style={{
                          background: "none",
                          border: `1px solid ${BORDER}`,
                          cursor: "pointer",
                          color: "#6B7280",
                          padding: "6px 8px",
                          borderRadius: 6,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                        title="Tilldela användare"
                      >
                        <FaUserPlus />
                        <span style={{ fontSize: 12 }}>Tilldela</span>
                      </button>

                      <button
                        onClick={() => openEdit(g)}
                        style={{
                          background: "none",
                          border: `1px solid ${BORDER}`,
                          cursor: "pointer",
                          color: "#6B7280",
                          padding: "6px 8px",
                          borderRadius: 6,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                        title="Redigera"
                      >
                        <FaEdit />
                        <span style={{ fontSize: 12 }}>Redigera</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal (Create/Edit) */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal} style={overlayStyle}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={modalStyle}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  display: "grid",
                  placeItems: "center",
                  background: "rgba(92,174,246,.15)",
                  color: ACCENT,
                }}
              >
                <FaLayerGroup />
              </div>
              <h4 style={{ margin: 0, color: "#111827" }}>
                {editingGroup ? "Redigera projektgrupp" : "Ny projektgrupp"}
              </h4>
            </div>

            <form onSubmit={onSubmit}>
              <label style={labelStyle}>Namn</label>
              <input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Ange gruppnamn"
                required
                style={inputStyle}
              />

              <div style={{ marginTop: 14 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <label style={labelStyle}>Projekt i gruppen</label>
                  <span style={{ ...countBadgeStyle, height: 20 }}>
                    {selectedProjectIds.length}
                  </span>
                </div>

                {/* Project search + dropdown icon */}
                <div style={{ position: "relative", marginBottom: 8 }}>
                  <FaSearch
                    style={{
                      position: "absolute",
                      left: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: TEXT_MUTED,
                      fontSize: 12,
                    }}
                  />
                  <input
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    placeholder="filtrera projekt…"
                    style={{ ...inputStyle, paddingLeft: 30 }}
                  />
                  <FaChevronDown
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#9CA3AF",
                      fontSize: 12,
                    }}
                  />
                </div>

                {/* Selectable list */}
                <div
                  style={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: 10,
                    maxHeight: 220,
                    overflowY: "auto",
                    padding: 8,
                    background: "#fff",
                  }}
                >
                  {projectOptions.length === 0 ? (
                    <p style={{ color: TEXT_MUTED, fontSize: 13, margin: 8 }}>
                      Inga projekt matchar filtret.
                    </p>
                  ) : (
                    projectOptions.map((p) => {
                      const id = String(p._id);
                      const checked = selectedProjectIds.includes(id);
                      return (
                        <label
                          key={id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "6px 8px",
                            borderRadius: 8,
                            cursor: "pointer",
                            background: checked
                              ? "rgba(92,174,246,.10)"
                              : "transparent",
                          }}
                          title={p.name}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleProjectSelection(id)}
                          />
                          <span
                            style={{
                              fontSize: 13,
                              color: "#111827",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {p.name}
                          </span>
                          {p.location ? (
                            <span
                              style={{
                                fontSize: 12,
                                color: TEXT_MUTED,
                                marginLeft: "auto",
                              }}
                            >
                              {p.location}
                            </span>
                          ) : null}
                        </label>
                      );
                    })
                  )}
                </div>

                {/* Always-visible list of selected projects */}
                <div
                  style={{
                    marginTop: 12,
                    background: "#fff",
                    border: `1px solid ${BORDER}`,
                    borderRadius: 10,
                    padding: 10,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <strong style={{ fontSize: 13, color: "#111827" }}>
                      Valda projekt
                    </strong>
                    {selectedProjects.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedProjectIds([])}
                        style={{
                          background: "none",
                          border: "none",
                          color: TEXT_MUTED,
                          cursor: "pointer",
                          fontSize: 12,
                          textDecoration: "underline",
                        }}
                        title="Töm val"
                      >
                        Rensa alla
                      </button>
                    )}
                  </div>

                  {selectedProjects.length === 0 ? (
                    <span style={{ fontSize: 12, color: TEXT_MUTED }}>
                      Inga projekt valda ännu.
                    </span>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      {selectedProjects.map((p) => (
                        <span
                          key={p._id}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 10px",
                            borderRadius: 999,
                            border: `1px solid ${BORDER}`,
                            background: "rgba(92,174,246,.08)",
                            color: "#111827",
                            fontSize: 12,
                            maxWidth: 260,
                          }}
                          title={p.name}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {p.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleProjectSelection(p._id)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#6B7280",
                              display: "grid",
                              placeItems: "center",
                              padding: 0,
                            }}
                            title="Ta bort"
                          >
                            <FaTimes />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 16, alignItems: "center" }}>
                <button type="submit" style={primaryBtnStyle}>
                  {editingGroup ? "Spara ändringar" : "Skapa grupp"}
                </button>

                {editingGroup && (
                  <button
                    type="button"
                    onClick={() => deleteGroup(editingGroup)}
                    style={{
                      background: "#fff",
                      color: "#B91C1C",
                      border: "1px solid #FCA5A5",
                      borderRadius: 10,
                      padding: "10px 14px",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                    title="Radera grupp"
                  >
                    <FaTrash />
                    Radera
                  </button>
                )}

                <button type="button" onClick={closeModal} style={ghostBtnStyle}>
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NEW: Assign Modal (Tilldela användare) */}
      {isAssignOpen && (
        <div className="modal-overlay" onClick={closeAssign} style={overlayStyle}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={modalStyle}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  display: "grid",
                  placeItems: "center",
                  background: "rgba(92,174,246,.15)",
                  color: ACCENT,
                }}
              >
                <FaUserPlus />
              </div>
              <h4 style={{ margin: 0, color: "#111827" }}>
                Tilldela användare till: {assignGroup?.name || "—"}
              </h4>
            </div>

            <div style={{ marginBottom: 10, fontSize: 12, color: TEXT_MUTED }}>
              Lägg till användare på alla projekt i den här gruppen. (Endast lägg till – inga borttagningar.)
            </div>

            {/* Search users */}
            <div style={{ position: "relative", marginBottom: 8 }}>
              <FaSearch
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: TEXT_MUTED,
                  fontSize: 12,
                }}
              />
              <input
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                placeholder="sök användare…"
                style={{
                  width: "100%",
                  height: 36,
                  padding: "0 12px 0 30px",
                  borderRadius: 8,
                  border: `1px solid ${BORDER}`,
                  outline: "none",
                  fontSize: 13,
                  background: "#fff",
                }}
              />
            </div>

            {/* Users list */}
            <div
              style={{
                border: `1px solid ${BORDER}`,
                borderRadius: 10,
                maxHeight: 260,
                overflowY: "auto",
                padding: 8,
                background: "#fff",
              }}
            >
              {filteredEmployees.length === 0 ? (
                <p style={{ color: TEXT_MUTED, fontSize: 13, margin: 8 }}>
                  Inga användare matchar filtret.
                </p>
              ) : (
                filteredEmployees.map((u) => {
                  const id = String(u._id || u.id);
                  const checked = selectedUserIds.includes(id);
                  return (
                    <label
                      key={id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 8px",
                        borderRadius: 8,
                        cursor: "pointer",
                        background: checked ? "rgba(92,174,246,.10)" : "transparent",
                      }}
                      title={u?.name || u?.email}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleUserSelection(id)}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          color: "#111827",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {u?.name || u?.email || "Okänd"}
                      </span>
                      {u?.email ? (
                        <span
                          style={{
                            fontSize: 12,
                            color: TEXT_MUTED,
                            marginLeft: "auto",
                          }}
                        >
                          {u.email}
                        </span>
                      ) : null}
                    </label>
                  );
                })
              )}
            </div>

            {/* Selected count + clear */}
            <div
              style={{
                marginTop: 12,
                background: "#fff",
                border: `1px solid ${BORDER}`,
                borderRadius: 10,
                padding: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <strong style={{ fontSize: 13, color: "#111827" }}>
                  Valda användare
                </strong>
                {selectedUserIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedUserIds([])}
                    style={{
                      background: "none",
                      border: "none",
                      color: TEXT_MUTED,
                      cursor: "pointer",
                      fontSize: 12,
                      textDecoration: "underline",
                    }}
                    title="Töm val"
                  >
                    Rensa alla
                  </button>
                )}
              </div>

            {/* Always-visible list of selected users */}
            {selectedUserIds.length === 0 ? (
            <span style={{ fontSize: 12, color: TEXT_MUTED }}>
                Inga användare valda ännu.
            </span>
            ) : (
            <>
                {/* Count */}
                <div
                style={{
                    fontSize: 12,
                    color: TEXT_MUTED,
                    marginBottom: 6,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                }}
                >
                <span style={{ fontWeight: 600, color: "#111827" }}>
                    {selectedUserIds.length}
                </span>{" "}
                st valda
                </div>

                {/* Selected chips */}
                <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                }}
                >
                {selectedUserIds.map((uid) => {
                    const u = employees.find((e) => String(e._id) === String(uid));
                    if (!u) return null;
                    return (
                    <span
                        key={uid}
                        style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "6px 10px",
                        borderRadius: 8, // ⬅️ square-ish look
                        border: `1px solid ${BORDER}`,
                        background: "rgba(92,174,246,.08)",
                        color: "#111827",
                        fontSize: 12,
                        maxWidth: 260,
                        height: 28,
                        }}
                        title={u.name || u.email}
                    >
                        <span
                        style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        >
                        {u.name || u.email || "Okänd"}
                        </span>
                        <button
                        type="button"
                        onClick={() => toggleUserSelection(uid)}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#6B7280",
                            display: "grid",
                            placeItems: "center",
                            padding: 0,
                            fontSize: 12,
                        }}
                        title="Ta bort"
                        >
                        <FaTimes />
                        </button>
                    </span>
                    );
                })}
                </div>
            </>
            )}


            </div>

            {/* Footer */}
            <div style={{ display: "flex", gap: 10, marginTop: 16, alignItems: "center" }}>
              <button
                type="button"
                onClick={assignUsersToGroup}
                style={primaryBtnStyle}
                title="Tilldela"
              >
                Tilldela
              </button>
              <button type="button" onClick={closeAssign} style={ghostBtnStyle}>
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast
        message={toastMessage}
        type={toastType}
        onClose={() => setToastMessage("")}
      />
    </div>
  );
};

// ---------- styles ----------
const labelStyle = { fontSize: 13, color: "#374151", display: "block", marginBottom: 6 };
const inputStyle = {
  width: "100%",
  height: 36,
  padding: "0 12px",
  borderRadius: 8,
  border: "1px solid #E5E7EB",
  outline: "none",
  fontSize: 13,
  background: "#fff",
};
const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.5)",
  display: "grid",
  placeItems: "center",
  zIndex: 1000,
};
const modalStyle = {
  width: "min(720px, 92vw)",
  background: "#F9FAFB",
  border: "1px solid #E5E7EB",
  borderRadius: 16,
  padding: 16,
  boxShadow: "0 12px 32px rgba(0,0,0,.25)",
  maxHeight: "82vh",
  overflowY: "auto",
};
const primaryBtnStyle = {
  background: "#5caef6ff",
  color: "#fff",
  border: "none",
  borderRadius: 10,
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 6px 14px rgba(92,174,246,.25)",
};
const ghostBtnStyle = {
  background: "#fff",
  color: "#374151",
  border: "1px solid #E5E7EB",
  borderRadius: 10,
  padding: "10px 14px",
  fontWeight: 600,
  cursor: "pointer",
};

export default ProjecGroups;
